import { useContext } from 'react';
import type {
  ColDef,
  ColumnMenuTab,
  ICellRendererParams
} from 'ag-grid-community';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getColor } from '@/helpers/utils';
import { RemoveAiKeywordsContext } from '@/context/Context';
import type { AiKeywordHistoryItem } from '@/app/KeywordResearch/types/aiKeywordsApiTypes';

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

const typeOptionsMap: Record<string, string> = {
  I: 'اطلاعاتی (I)',
  N: 'مسیر (N)',
  T: 'تراکنشی (T)',
  C: 'تجاری (C)',
  'long-tail': 'طولانی',
  trending: 'ترندی',
  'high-search': 'جستجوی بالا',
  lsi: 'LSI',
  'content-idea': 'ایده محتوا',
  local: 'محلی',
  'low-competition': 'کم رقابت',
  fomo: 'FOMO',
  question: 'سوالی',
  suggested: 'پیشنهادی'
};

type GeoLocationItem = {
  code: string;
  fa_name: string;
};

type GeoLanguageItem = {
  code: string;
  fa_name: string;
};

type AiKeywordHistoryRow = AiKeywordHistoryItem & {
  num_of_keywords?: number;
};

type AiKeywordsHistoryColumn = ColDef<AiKeywordHistoryRow> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
};

type RemoveAiKeywordsContextValue = {
  setId: (id: string) => void;
  setSubject: (subject: string) => void;
  setShow: (show: boolean) => void;
};

export const aiKeywordsHistoryColumns = (
  domain_slug: string,
  domain: string,
  geoLocationsData: GeoLocationItem[],
  geoLanguagesData: GeoLanguageItem[]
): AiKeywordsHistoryColumn[] => [
  {
    field: 'subject',
    type: 'string',
    title: 'عنوان سئو',
    width: 100,
    searchable: false,
    menuTabs,
    floatingFilter: false,
    minWidth: 250
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'keywords',
    type: 'array',
    title: 'کلمات',
    cellRenderer: (params: ICellRendererParams<AiKeywordHistoryRow>) => {
      const keywordsText = Array.isArray(params.value)
        ? params.value.join(', ')
        : params.value || '';
      return (
        <Link
          to={`/keyword-research/ai-keywords/details/${params.data?.id}/${domain_slug}/${domain}`}
          style={{
            color: getColor('navbar-vertical-default-link-active-color')
          }}
          className={`fw-bolder btn-link ${!params.data?.id && 'disabled-link'}`}
        >
          <div className="d-flex gap-1 align-items-baseline">
            {decodeURIComponent(keywordsText)}
          </div>
        </Link>
      );
    },
    flex: 1,
    minWidth: 200
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'content',
    type: 'string',
    title: 'هدف',
    width: 150,
    cellRenderer: (params: ICellRendererParams<AiKeywordHistoryRow>) =>
      decodeURIComponent(params.value || '')
  },
  {
    searchable: true,
    menuTabs,
    floatingFilter: false,
    field: 'country',
    type: 'string',
    title: 'مکان جستجو',
    width: 125,
    cellRenderer: (params: ICellRendererParams<AiKeywordHistoryRow>) => {
      const countryCode = params.value?.toLowerCase() || params.value;
      const location = geoLocationsData?.find(
        item => item.code?.toLowerCase() === countryCode
      );
      if (!location) return params.value;
      return (
        <div className="d-flex gap-2 align-items-center">
          <img
            src={`https://static.mizfa.tools/flags/${countryCode}.webp`}
            alt={location.fa_name}
          />
          {location.fa_name}
        </div>
      );
    }
  },
  {
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: false,
    field: 'language',
    type: 'string',
    title: 'زبان جستجو',
    width: 118,
    cellRenderer: (params: ICellRendererParams<AiKeywordHistoryRow>) => {
      const language = geoLanguagesData?.find(
        item => item.code === params.value
      );
      return language ? language.fa_name : params.value;
    }
  },
  {
    field: 'num_of_keywords',
    type: 'number',
    title: 'تعداد کلمات',
    width: 130,
    searchable: true,
    menuTabs,
    floatingFilter: false
  },
  {
    field: 'type',
    type: 'array',
    title: 'نوع تحقیق',
    width: 200,
    searchable: false,
    menuTabs,
    floatingFilter: false,
    cellRenderer: (params: ICellRendererParams<AiKeywordHistoryRow>) => {
      if (!Array.isArray(params.value)) return '';
      return params.value
        .map(typeValue => typeOptionsMap[typeValue] || typeValue)
        .join(', ');
    }
  },
  {
    field: 'url',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: ICellRendererParams<AiKeywordHistoryRow>) => {
      const {
        setId: setRemId,
        setSubject: setRemSubject,
        setShow
      } = useContext(RemoveAiKeywordsContext) as RemoveAiKeywordsContextValue;

      return (
        <span className="allaho_akbar_zIndex">
          <Dropdown
            onClick={e => e.stopPropagation()}
            align="end"
            className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item
                as={Link}
                to={`/keyword-research/ai-keywords/details/${params.data?.id}/${domain_slug}/${domain}`}
                className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                مشاهده
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  const keywordsText = Array.isArray(params.data?.keywords)
                    ? params.data.keywords.join(', ')
                    : params.data?.keywords || '';
                  setRemSubject(decodeURIComponent(keywordsText));
                  setRemId(params.data?.id ?? '');
                  setShow(true);
                }}
                className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                حذف
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </span>
      );
    }
  }
];
